import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { openWhatsApp } from "../utils/callUtils";
import {
  PRESET_MESSAGES_ROW1,
  PRESET_MESSAGES_ROW2,
} from "../constants/presetMessages";
import "./Login.css";

const Login = ({ asBottomSheet = false, isOpen = true, onClose }) => {
  const navigate = useNavigate();
  const { sendWhatsAppOTP, login, loginAsGuest } = useAuth();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("971");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("phone"); // "phone" or "otp"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (asBottomSheet && isOpen) {
      setPhoneNumber("");
      setCountryCode("971");
      setStep("phone");
      setOtp("");
      setError("");
    }
  }, [asBottomSheet, isOpen]);

  const countryFlags = {
    971: "🇦🇪", // UAE
    1: "🇺🇸", // USA/Canada
    44: "🇬🇧", // UK
    91: "🇮🇳", // India
  };

  const handleSendOTP = async () => {
    if (!phoneNumber || phoneNumber.trim() === "") {
      setError("Please enter a valid phone number");
      return;
    }

    setError("");
    setLoading(true);

    const fullNumber = `${countryCode}${phoneNumber}`;

    const result = await sendWhatsAppOTP(fullNumber);

    setLoading(false);

    if (result.success) {
      setStep("otp");
    } else {
      setError(result.error || "Failed to send OTP. Please try again.");
    }
  };

  const handleOtpSubmit = async () => {
    if (!otp || otp.trim() === "") {
      setError("Please enter the OTP");
      return;
    }

    setError("");
    setLoading(true);

    const fullNumber = `${countryCode}${phoneNumber}`;
    const result = await login(fullNumber, otp);

    setLoading(false);

    if (result.success) {
      localStorage.setItem("phoneNumber", fullNumber);
      if (asBottomSheet) {
        if (onClose) {
          onClose();
        }
      } else {
        navigate("/");
      }
    } else {
      setError(result.error || "Invalid OTP. Please try again.");
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    const result = await loginAsGuest();
    setLoading(false);

    if (result.success) {
      if (asBottomSheet && onClose) {
        onClose();
        return;
      }
      navigate("/");
    } else {
      setError(result.error || "Failed to login as guest. Please try again.");
    }
  };

  const handleOpenWhatsApp = () => {
    const randomPool = [...PRESET_MESSAGES_ROW1, ...PRESET_MESSAGES_ROW2].filter(
      (item) => item.type !== "call"
    );
    const randomMessage =
      randomPool[Math.floor(Math.random() * randomPool.length)]?.text || "";
    openWhatsApp(randomMessage);
  };

  if (asBottomSheet && !isOpen) {
    return null;
  }

  return (
    <div className={asBottomSheet ? "login-sheet-overlay" : ""}>
      {asBottomSheet && (
        <button
          type="button"
          className="login-sheet-backdrop"
          aria-label="Close login"
          onClick={handleSkip}
        />
      )}
      <div
        className={`login-container ${asBottomSheet ? "login-container-sheet" : ""}`}
      >
        {asBottomSheet && <div className="login-sheet-handle" />}
        <div className="login-header">
          {step === "phone" ? (
            <>
              <div className="login-contact-card">
                <div
                  className="login-contact-main"
                  role="button"
                  tabIndex={0}
                  onClick={handleOpenWhatsApp}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpenWhatsApp();
                    }
                  }}
                >
                  <div className="login-contact-avatar-wrap">
                    <div className="login-contact-avatar">
                      <img
                        src="https://jdblob.blob.core.windows.net/img/comp/dubaimall.jpg"
                        alt="The Dubai Mall"
                      />
                    </div>
                  </div>
                  <div className="login-contact-meta">
                    <span className="login-contact-name">
                      The Dubai Mall Concierge
                    </span>
                    <div className="login-contact-status-row">
                      <span className="login-contact-status">Online</span>
                      <span className="login-contact-status-soft">
                        Replies instantly
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="login-contact-start-button"
                  onClick={handleOpenWhatsApp}
                >
                  <img src="/icon-wa.svg" alt="" />
                  <span>Continue in WhatsApp</span>
                </button>
              </div>
              <div className="login-or-divider">
                <span>Or</span>
              </div>
              <h3 className="login-heading">Connect your number</h3>
            </>
          ) : (
            <h2 className="login-heading">Enter OTP</h2>
          )}
        </div>

        <div className="login-content">
          <p className="login-description">
            {step === "phone" ? (
              <>
                As well from the app, your <i>Personal Mall Concierge</i> will
                be available for direct <b>talk</b>, <b>chat</b>, <b>call</b> or{" "}
                <b>WhatsApp</b>.
              </>
            ) : (
              <>
                We've sent a verification code to{" "}
                <b>
                  {countryCode}
                  {phoneNumber}
                </b>
                .
              </>
            )}
          </p>

          {error && <div className="error-message">{error}</div>}

          {step === "phone" ? (
            <>
              <div className="phone-input-section">
                <div className="phone-input-container">
                  <div className="country-code-selector">
                    <span className="flag">
                      {countryFlags[countryCode] || "🇦🇪"}
                    </span>
                    <select
                      className="country-code"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      disabled={loading}
                    >
                      <option value="971">971</option>
                      <option value="1">1</option>
                      <option value="44">44</option>
                      <option value="91">91</option>
                    </select>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="dropdown-arrow"
                    >
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    className="phone-input"
                    placeholder="Enter mobile number"
                    value={phoneNumber}
                    onChange={(e) =>
                      setPhoneNumber(e.target.value.replace(/\D/g, ""))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !loading && phoneNumber) {
                        handleSendOTP();
                      }
                    }}
                    disabled={loading}
                  />
                </div>
                <button
                  className="send-otp-button"
                  onClick={handleSendOTP}
                  disabled={loading || !phoneNumber}
                >
                  {loading ? "Sending..." : "Send WhatsApp OTP"}
                </button>
              </div>

              <div className="skip-button-container">
                <button
                  className="skip-button"
                  onClick={handleSkip}
                  disabled={loading}
                >
                  Skip, I'll login later
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="phone-input-section">
                <label className="phone-label">Enter OTP</label>
                <div className="otp-input-container">
                  <input
                    type="text"
                    className="otp-input"
                    placeholder="Enter 6-digit code"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !loading && otp.length >= 4) {
                        handleOtpSubmit();
                      }
                    }}
                    maxLength="6"
                    disabled={loading}
                    autoFocus
                  />
                </div>
              </div>

              <button
                className="submit-button"
                onClick={handleOtpSubmit}
                disabled={loading || otp.length < 4}
              >
                {loading ? "Verifying..." : "Verify & Sign In"}
              </button>

              <button
                className="resend-button"
                onClick={handleSendOTP}
                disabled={loading}
              >
                Resend OTP
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
